import PropTypes from 'prop-types';
import { Container, Row, Col } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import React from 'react';

import SEO from '@/components/common/SEO';

import TermsAndConditions from './TermsAndConditions';
import PrivacyPolicy from './PrivacyPolicy';
import CookiePolicy from './CookiePolicy';
import AcceptableUse from './AcceptableUse';
import TakedownPolicy from './TakedownPolicy';
import ContentModerationPolicy from './ContentModerationPolicy';

import './Policies.css';

export default function Policies() {
    const location = useLocation();
    const navigate = useNavigate();

    const isOpen = (slug) => location.pathname === `/policies/${slug}`;

    const PolicyBox = ({ title, slug, children }) => {
        const handleToggle = () => {
            navigate(isOpen(slug) ? '/policies' : `/policies/${slug}`);
        };

        return (
            <div className="policy-item text-start bg-white p-4 mb-4 shadow-sm rounded">
                <div
                    className="d-flex justify-content-between align-items-center policy-header"
                    onClick={handleToggle}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleToggle();
                        }
                    }}
                >
                    <h3 className="text-primary mb-0 fw-bold fs-5">{title}</h3>
                    <span className="toggle-button fw-bold fs-4 text-dark">
                        {isOpen(slug) ? '×' : '+'}
                    </span>
                </div>

                {isOpen(slug) && (
                    <div className="mt-3 policy-content lh-base text-body fs-6">
                        {children}
                    </div>
                )}
            </div>
        );
    };

    PolicyBox.propTypes = {
        title: PropTypes.string.isRequired,
        slug: PropTypes.string.isRequired,
        children: PropTypes.node.isRequired,
    };

    const seoDescription =
        "Review Sustainability Hub's policies including Terms and Conditions, " +
        'Privacy Policy, Cookie Policy, Acceptable Use Policy, Content Reporting & Takedown Policy, ' +
        'and Content Moderation Policy. We are committed to transparency and ethical practices.';

    return (
        <section id="policies" className="page-container py-5 bg-light">
            <SEO
                title="Policies"
                description={seoDescription}
                canonical="/policies"
                breadcrumbs={[
                    { name: 'Home', path: '/' },
                    { name: 'Policies', path: '/policies' },
                ]}
            />
            <Container className="policies-container">
                <Row>
                    <Col className="policies-content">
                        <h2 className="h1 text-primary mb-4 text-center fw-bold">
                            Welcome to Our Policies Page
                        </h2>
                        <p className="fs-5 text-start mb-5 lh-lg text-body">
                            At <strong>Sustainability Hub</strong>, we are
                            committed to transparency, ethical practices, and
                            earning your trust. This section outlines the key
                            policies that guide how we handle your information
                            and support your experience on our platform. Please
                            select a policy below to learn more about your
                            rights and our responsibilities.
                        </p>
                        <PolicyBox title="Terms and Conditions" slug="terms">
                            <TermsAndConditions />
                        </PolicyBox>
                        <PolicyBox title="Privacy Policy" slug="privacy">
                            <PrivacyPolicy />
                        </PolicyBox>
                        <PolicyBox title="Cookie Policy" slug="cookies">
                            <CookiePolicy />
                        </PolicyBox>
                        <PolicyBox
                            title="Acceptable Use Policy"
                            slug="acceptable-use"
                        >
                            <AcceptableUse />
                        </PolicyBox>
                        <PolicyBox
                            title="Content Reporting & Takedown Policy"
                            slug="takedown"
                        >
                            <TakedownPolicy />
                        </PolicyBox>
                        <PolicyBox
                            title="Content Moderation Policy"
                            slug="moderation"
                        >
                            <ContentModerationPolicy />
                        </PolicyBox>
                    </Col>
                </Row>
            </Container>
        </section>
    );
}
